import { Button, Card, Col, DatePicker, Descriptions, Divider, Empty, Form, Input, InputNumber, message, Row, Select, Space } from "antd";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import Title from "antd/es/typography/Title";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  BlobFinanceConfigInput,
  StaffFinanceSettingsContainerBlobFinanceConfigFieldsFragment,
  StaffFinanceSettingsContainerBlobFinancialConfigFieldsFragment,
  StaffFinanceSettingsContainerStaffUserFieldsFragment,
} from "../../../../generated";
import { ApplicationTypeEnum } from "./finance-settings.enum";
import { DescriptionsItemType } from "antd/es/descriptions";
import { DATE_FORMAT, DATE_TIME_FORMAT, FormatDate } from "../../../../constants";

// Mapping for application type display names
const applicationTypeLabels = {
  [ApplicationTypeEnum.IdCase]: "ID Case",
  [ApplicationTypeEnum.CredentialVerificationCase]: "Credential Verification Case",
  [ApplicationTypeEnum.SendReportCase]: "Send Report Case",
};
export interface FinanceSettingsEditProps {
  data: StaffFinanceSettingsContainerBlobFinanceConfigFieldsFragment;
  financialSettingsList: StaffFinanceSettingsContainerBlobFinancialConfigFieldsFragment[];
  staffData: StaffFinanceSettingsContainerStaffUserFieldsFragment[];
  activeCaseType: string;
  onSave: (input: BlobFinanceConfigInput) => Promise<void>;
  onCancel: () => void;
}

export const FinanceSettingsEdit: React.FC<FinanceSettingsEditProps> = (props) => {
  const [form] = Form.useForm<BlobFinanceConfigInput>();
  const [formLoading, setFormLoading] = React.useState<boolean>(false);

  const handleFinish = async (values: BlobFinanceConfigInput) => {
    setFormLoading(true);
    try {
      console.log("form values: ", values);
      await props.onSave(values);
      message.success("Finance config updated successfully");
    } catch (error) {
      console.error("error: ", error);
      message.error("Failed to save the finance settings");
    } finally {
      setFormLoading(false);
    }
  };

  const versionInfo:DescriptionsItemType[] = [
    {
      key: "1",
      label: "Created by",
      children: props.staffData.find((staff) => staff.id === props.data.glConfig?.createdBy)?.displayName,
    },
    {
      key: "2",
      label: "Ver",
      children: props.data.glConfig?.version,
    },
    {
      key: "3",
      label: "Created at",
      children: FormatDate(props.data.glConfig?.createdAt, DATE_TIME_FORMAT),
    },
  ];

  const initializeFormData = (data: StaffFinanceSettingsContainerBlobFinanceConfigFieldsFragment) => ({
    id: data.id,
    caseType: props.activeCaseType,
    blobData: {
      effectiveAt: data.effectiveAt,
      glConfig: {
        note: data.glConfig?.note,
        version: data.glConfig?.version,
        createdBy: data.glConfig?.createdBy,
        createdAt: data.glConfig?.createdAt,
        submission: data.glConfig?.submission,
        recognition: data.glConfig?.recognition,
        refunds: data.glConfig?.refunds,
        // additionalCharges is an array of objects and to omit __typename we use filter
        additionalCharges:
          data.glConfig?.additionalCharges
            ?.filter((charge) => charge !== null)
            .map((charge) => ({ type: charge!.type, debitGLAccount: charge!.debitGLAccount, creditGLAccount: charge!.creditGLAccount })) || [],
      },
    },
  });

  useEffect(() => {
    const initialFormData = initializeFormData(props.data);
    form.setFieldsValue(initialFormData);
    console.log("initialFormData: ", initialFormData);
  }, [props.data]);

  const isEffectiveDateInFuture = dayjs(props.data.effectiveAt).isAfter(dayjs());
  const GL_ACCOUNT_PATTERN: RegExp = /^\d{3}-\d{4}-\d{3}$/;

  return (
    <Form form={form} layout="vertical" initialValues={initializeFormData(props.data)} onFinish={handleFinish}>
      <Descriptions title="Rule Details" items={versionInfo} />
      <br />
      <Card bordered={false} style={{ marginTop: 1, marginBottom: 10 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Rule Name" name={["blobData", "glConfig", "note"]} rules={[{ required: true, message: "Rule name is required" }]}>
              <Input placeholder="Rule Name" maxLength={100} allowClear />
            </Form.Item>
            <Form.Item name={["blobData", "glConfig", "version"]} hidden>
              <Input />
            </Form.Item>
            <Form.Item name={["blobData", "glConfig", "createdBy"]} hidden>
              <Input />
            </Form.Item>
            <Form.Item name={["blobData", "glConfig", "createdAt"]} hidden>
              <Input />
            </Form.Item>
            <Form.Item name={["id"]} hidden>
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Application Type" name={["caseType"]} rules={[{ required: true, message: "Application type is required" }]}>
              <Select value={props.activeCaseType} disabled>
                {Object.keys(applicationTypeLabels).map((applicationType) => (
                  <Select.Option key={applicationType} value={applicationType}>
                    {applicationTypeLabels[applicationType as ApplicationTypeEnum]}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Effective Date"
              name={["blobData", "effectiveAt"]}
              valuePropName="value"
              getValueProps={(value) => ({
                value: value ? dayjs(value) : "",
              })}
              getValueFromEvent={(date) => (date ? date.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : "")}
              rules={[{ required: true, message: "Effective date is required" }]}
            >
              <DatePicker
                value={dayjs(props.data.effectiveAt)}
                style={{ width: "75%" }}
                format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                disabled={!isEffectiveDateInFuture}
                disabledDate={(current) => {
                  const isPastDate = current && current < dayjs().endOf("day");
                  const isInFinancialSettingsList = props.financialSettingsList.some((item) => dayjs(item.effectiveAt).isSame(current, "day") && item.caseType === props.activeCaseType);
                  return isPastDate || isInFinancialSettingsList;
                }}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Title level={5} style={{ fontSize: "15px" }}>
        GL Account Settings
      </Title>
      {/* Submission, Recognition, Refunds Fields */}
      <Card bordered={false} style={{ marginTop: 16 }}>
        <Row gutter={16} align="middle" style={{ marginBottom: 10 }}>
          <Col span={4}></Col>
          <Col span={6}>
            <span style={{ color: "red" }}>*</span> Debit Account
          </Col>
          <Col span={6}>
            <span style={{ color: "red" }}>*</span> Credit Account
          </Col>
          <Col span={6}>
            <span style={{ color: "red" }}>*</span> Case Submission Charge
          </Col>
        </Row>
        <Row gutter={16} align="top">
          <Col span={4}>Submission :</Col>
          <Col span={6}>
            <Form.Item
              label=""
              name={["blobData", "glConfig", "submission", "debitGLAccount"]}
              rules={[
                { required: true, message: "Missing debit Account" },
                { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
              ]}
            >
              <Input placeholder="Debit Account" maxLength={12} disabled={!isEffectiveDateInFuture} allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label=""
              name={["blobData", "glConfig", "submission", "creditGLAccount"]}
              rules={[
                { required: true, message: "Missing credit Account" },
                { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
              ]}
            >
              <Input placeholder="Credit Account" maxLength={12} disabled={!isEffectiveDateInFuture} allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label=""
              name={["blobData", "glConfig", "submission", "amount"]}
              rules={[
                { required: true, message: "Missing amount" },
                { type: "number", min: 1, message: "Amount must be greater than 0" },
              ]}
            >
              <InputNumber prefix="$" placeholder="Amount" type="number" disabled={!isEffectiveDateInFuture} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16} align="top">
          <Col span={4}>Recognition :</Col>
          <Col span={6}>
            <Form.Item
              label=""
              name={["blobData", "glConfig", "recognition", "debitGLAccount"]}
              rules={[
                { required: true, message: "Missing debit Account" },
                { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
              ]}
            >
              <Input placeholder="Debit Account" maxLength={12} disabled={!isEffectiveDateInFuture} allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label=""
              name={["blobData", "glConfig", "recognition", "creditGLAccount"]}
              rules={[
                { required: true, message: "Missing credit Account" },
                { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
              ]}
            >
              <Input placeholder="Credit Account" maxLength={12} disabled={!isEffectiveDateInFuture} allowClear />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16} align="top">
          <Col span={4}>Refund :</Col>
          <Col span={6}>
            <Form.Item
              label=""
              name={["blobData", "glConfig", "refunds", "debitGLAccount"]}
              rules={[
                { required: true, message: "Missing debit Account" },
                { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
              ]}
            >
              <Input placeholder="Debit Account" maxLength={12} disabled={!isEffectiveDateInFuture} allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label=""
              name={["blobData", "glConfig", "refunds", "creditGLAccount"]}
              rules={[
                { required: true, message: "Missing credit Account" },
                { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
              ]}
            >
              <Input placeholder="Credit Account" maxLength={12} disabled={!isEffectiveDateInFuture} allowClear />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      {/* Editable List for Additional Charges */}
      <Card title="Additional Charges" bordered={false} style={{ marginTop: 16 }}>
        <Form.List name={["blobData", "glConfig", "additionalCharges"]}>
          {(fields, { add, remove }) => (
            <div>
              <Row id="additionalChargesHeader" gutter={8} style={{ textAlign: "center", marginBottom: 10 }}>
                <Col span={4}>Charge Type</Col>
                <Col span={4}>Debit Account</Col>
                <Col span={4}>Credit Account</Col>
              </Row>
              {fields.length === 0 ? (
                <Row>
                  <Col span={12}>
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  </Col>
                </Row>
              ) : (
                fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: "flex", marginBottom: -1 }} align="baseline">
                    <Form.Item {...restField} name={[name, "type"]} rules={[{ required: true, message: "Missing charge type" }]}>
                      <Input placeholder="Charge Type" maxLength={50} disabled={!isEffectiveDateInFuture} allowClear />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "debitGLAccount"]}
                      rules={[
                        { required: true, message: "Missing debit Account" },
                        { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
                      ]}
                    >
                      <Input placeholder="Debit Account" maxLength={12} disabled={!isEffectiveDateInFuture} allowClear />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "creditGLAccount"]}
                      rules={[
                        { required: true, message: "Missing credit Account" },
                        { pattern: GL_ACCOUNT_PATTERN, message: "Invalid account number. Expected xxx-xxxx-xxx" },
                      ]}
                    >
                      <Input placeholder="Credit Account" maxLength={12} disabled={!isEffectiveDateInFuture} allowClear />
                    </Form.Item>
                    <MinusCircleOutlined 
                      className="dynamic-delete-button"
                      onClick={() => {
                        if (!isEffectiveDateInFuture) return;
                        remove(name);
                      }} 
                      disabled={!isEffectiveDateInFuture}
                    />
                  </Space>
                ))
              )}
              <Row gutter={16} style={{ textAlign: "center" }}>
                <Col span={12}>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} disabled={!isEffectiveDateInFuture}>
                    Add additional charge
                  </Button>
                </Col>
              </Row>
            </div>
          )}
        </Form.List>
      </Card>
      <Divider />
      <Row justify="end" gutter={8}>
        <Col>
          <Button style={{ margin: "0 8px" }} onClick={props.onCancel} disabled={formLoading}>
            Cancel
          </Button>
        </Col>
        <Col>
          <Button type="primary" htmlType="submit" value={"save"} loading={formLoading}>
            Save
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

